"use client";

import { useRef } from "react";
import { ArrowRight, Activity, Brain, ChartLine as LineChart, ShieldAlert, FileChartColumn as FileBarChart, Network } from 'lucide-react';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const capabilities = [{
  icon: Activity,
  title: "Tecnologia de Pulverização para Snacks",
  description: "A aplicação de ingredientes, aromatizantes, revestimentos e agentes de desmoldagem é um desafio. E quanto mais espesso, pegajoso e viscoso for o material, maior é o desafio. A tecnologia de pulverização da Spraying Systems Co. pode ajudar a aplicar de forma precisa e eficiente os materiais de revestimento mais pesados e complexos — mesmo aqueles que possa ter considerado demasiado espessos para aplicação por pulverização. Quer esteja a trabalhar com chocolate, caramelo, xaropes ou líquidos espessos, os nossos especialistas locais em pulverização estão aqui para ajudar. Utilizamos tecnologia de pulverização para garantir que os revestimentos são aplicados da forma correta — de forma rápida e precisa.",
  inverted: false
}, {
  icon: Brain,
  title: "Revestir sem compromissos",
  description: "Enfrentar revestimentos difíceis não significa ter de sacrificar a qualidade ou abrandar a produção. Compreendemos — aplicar ingredientes, aromatizantes, revestimentos e agentes de desmoldagem, especialmente os mais espessos e pegajosos, pode ser complicado. Quando os revestimentos não são bem aplicados, desperdiçam-se materiais caros, prejudica-se a qualidade do produto, aumentam as taxas de rejeição, criam-se riscos de segurança e a limpeza torna-se um grande problema. Muitas unidades focam-se nos “sintomas” de um revestimento ineficaz em vez de resolverem as causas de raiz. Conter a sujidade não resolve o problema.",
  inverted: false
}, {
  icon: LineChart,
  title: "Limpeza Eficiente de Revestimentos Pesados",
  description: "Os revestimentos pesados são mais difíceis de limpar — e os revestimentos secos, cozidos ou caramelizados representam um desafio ainda maior.A limpeza de correias transportadoras, tanques e equipamentos cobertos com resíduos espessos e pesados é difícil. A sobredosagem só torna a limpeza ainda mais complicada, especialmente depois de os revestimentos terem secado, cozido ou caramelizado. Isto pode fazer com que inundar o equipamento e as superfícies com cada vez mais água ou produtos químicos pareça uma abordagem razoável. Mas a limpeza eficaz não exige exageros. Se é difícil de aplicar, será difícil de remover. Os nossos bicos, limpa-tanques e pistolas de pulverização manuais tornam a limpeza mais eficiente e menos intensiva em recursos, ao direcionarem a ação para as zonas que necessitam de maior atenção.",
  inverted: true
}];
export default function TechnicalAdvantages() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(min-width: 1024px) and (prefers-reduced-motion: no-preference)", () => {
      const leftCol = el.querySelector<HTMLElement>(".engine-left");
      const rightCol = el.querySelector<HTMLElement>(".engine-right");
      if (!leftCol || !rightCol) return;
      ScrollTrigger.create({
        trigger: rightCol,
        start: "top top+=120",
        endTrigger: rightCol,
        end: "bottom bottom-=120",
        pin: leftCol,
        pinSpacing: false
      });
      gsap.set(".engine-card", {
        y: 32
      });
      gsap.to(".engine-card", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: {
          trigger: rightCol,
          start: "top 75%",
          once: true
        }
      });
      gsap.set([".engine-eyebrow", ".engine-title", ".engine-lede", ".engine-cta"], {
        y: 20
      });
      gsap.to([".engine-eyebrow", ".engine-title", ".engine-lede", ".engine-cta"], {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(max-width: 1023px) and (prefers-reduced-motion: no-preference)", () => {
      gsap.set(".engine-card", {
        y: 24
      });
      gsap.to(".engine-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set([".engine-eyebrow", ".engine-title", ".engine-lede", ".engine-cta"], {
        y: 16
      });
      gsap.to([".engine-eyebrow", ".engine-title", ".engine-lede", ".engine-cta"], {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".engine-card", ".engine-eyebrow", ".engine-title", ".engine-lede", ".engine-cta"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31224" id="technicaladvantages" data-nav="dark" className="relative overflow-hidden bg-background text-foreground py-32 md:py-44">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 h-[1200px] w-[1200px] rounded-lg" style={{
      background: "radial-gradient(circle at center, hsl(215 80% 20% / 0.15) 0%, hsl(215 80% 20% / 0.06) 35%, transparent 70%)"
    }} />

      <div ref={container} className="relative mx-auto w-full max-w-[1360px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
          <div className="engine-left lg:col-span-5">
            <div className="relative">
              <div aria-hidden="true" className="pointer-events-none absolute -top-16 -left-10 h-[200px] w-[200px] rounded-lg blur-3xl" style={{
              background: "hsl(215 90% 45% / 0.18)"
            }} />

              <div className="engine-eyebrow relative text-xs font-medium uppercase" style={{
              opacity: 0,
              color: "var(--muted-foreground)",
              letterSpacing: "0.22em"
            }}>
                Eurocontrol
              </div>

              <h2 className="engine-title relative mt-6 font-default font-medium text-foreground" style={{
              opacity: 0,
              fontSize: "clamp(2rem, 4.6vw, 4rem)",
              letterSpacing: "-0.03em",
              lineHeight: 1.05
            }}>Melhore a Sua Operação de Revestimento</h2>

              <p className="engine-lede relative mt-8 max-w-[400px] font-default" style={{
              opacity: 0,
              color: "var(--muted-foreground)",
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              fontWeight: 350,
              lineHeight: 1.55,
              letterSpacing: "-0.005em"
            }}>Se está a revestir produtos em correias transportadoras ou em tambores e ainda depende de métodos antigos de enrobing ou aplicação manual, a tecnologia de pulverização pode oferecer-lhe uma forma mais rápida, eficaz e eficiente de o fazer.</p>

              <div className="engine-cta relative mt-10" style={{
              opacity: 0
            }}>
                <Link to="/ProductsPage" className="group inline-flex items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-primary">
                  <span>Conheça as soluções</span>
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-[240ms] ease-out group-hover:translate-x-1.5" style={{
                  color: "var(--primary)"
                }} />
                </Link>
              </div>
            </div>
          </div>

          <div className="engine-right flex flex-col gap-8 lg:col-span-7">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            const inverted = cap.inverted;
            return <div key={i} data-index={i} className={`engine-card group relative overflow-hidden rounded-2xl p-8 md:p-9 transition-all duration-[320ms] ease-out ${inverted ? "bg-primary text-primary-foreground" : "bg-card backdrop-blur-2xl"}`} style={{
              opacity: 0,
              border: inverted ? "1px solid var(--primary)" : "1px solid var(--border)"
            }}>
                  {!inverted && <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-[320ms] group-hover:opacity-100" style={{
                boxShadow: "inset 0 0 80px hsl(215 90% 45% / 0.08)",
                borderRadius: "inherit"
              }} />}

                  <div className="relative flex items-start gap-6">
                    <div className={`flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full ${inverted ? "bg-primary-foreground/10" : "bg-muted"}`} style={{
                  border: inverted ? "1px solid var(--primary-foreground)" : "1px solid var(--border)"
                }}>
                      <Icon className="h-7 w-7" strokeWidth={1.5} style={{
                    color: inverted ? "var(--primary-foreground)" : "var(--primary)"
                  }} />
                    </div>

                    <div className="flex-1 pt-2">
                      <h3 className="font-default font-medium" style={{
                    fontSize: "clamp(1.25rem, 1.8vw, 1.625rem)",
                    letterSpacing: "-0.015em",
                    lineHeight: 1.2,
                    color: inverted ? "var(--primary-foreground)" : "var(--foreground)"
                  }}>
                        {cap.title}
                      </h3>
                      <p className="mt-3 font-default" style={{
                    fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                    fontWeight: 350,
                    lineHeight: 1.6,
                    color: inverted ? "var(--primary-foreground)" : "var(--muted-foreground)"
                  }}>
                        {cap.description}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}